/**
 * Curated fields for the visual admin (“customiser”).
 * Paths: translation = under payload[lang]; contact = contactInfo; servicePage = servicePages[lang][slug].
 */

/** @typedef {'text' | 'textarea' | 'image'} FieldType */

/**
 * @typedef {Object} Field
 * @property {string} path
 * @property {string} label
 * @property {FieldType} [type]
 * @property {string} [hint] — dimensiuni recomandate / note pentru admin
 */

/**
 * @typedef {Object} ListField
 * @property {string} key
 * @property {string} label
 * @property {'text' | 'textarea' | 'image' | 'video' | 'select'} [type]
 * @property {string[]} [options]
 * @property {string} [hint]
 */

/**
 * @typedef {Object} ListEditorConfig
 * @property {string} path — dot path to array under translation domain
 * @property {string} itemLabel
 * @property {ListField[]} itemFields
 * @property {number} [minItems]
 * @property {number} [maxItems]
 */

/**
 * @typedef {Object} ToggleField
 * @property {string} path — under ro.homepageSettings.*
 * @property {string} label
 */

/**
 * @typedef {Object} Section
 * @property {string} id
 * @property {string} page — CUSTOMIZER_PAGES id
 * @property {string} title
 * @property {string} [subtitle]
 * @property {'translation' | 'contact' | 'servicePage'} domain
 * @property {string} [slug]
 * @property {Field[]} [fields]
 * @property {ListEditorConfig} [listEditor]
 * @property {ListEditorConfig[]} [listEditors]
 * @property {ToggleField[]} [toggles]
 */

export const CUSTOMIZER_PAGES = [
  { id: "global", title: "Global", href: null },
  { id: "home", title: "Acasă", href: "/" },
  { id: "about", title: "Despre", href: "/despre" },
  { id: "services", title: "Servicii", href: "/servicii" },
  { id: "portfolio", title: "Portofoliu", href: "/portofoliu" },
  { id: "contact", title: "Contact", href: "/contact" },
  { id: "legal", title: "Politici", href: "/politica-confidentialitate" },
];

const cookieCategoryFields = [
  {
    key: "id",
    label: "ID categorie (fix)",
    type: "select",
    options: ["necessary", "preferences", "statistics", "marketing"],
  },
  { key: "name", label: "Nume categorie" },
  { key: "description", label: "Descriere categorie", type: "textarea" },
];

const cookieItemFields = [
  {
    key: "category",
    label: "Categorie",
    type: "select",
    options: ["necessary", "preferences", "statistics", "marketing"],
  },
  { key: "name", label: "Nume cookie" },
  { key: "provider", label: "Furnizor" },
  { key: "purpose", label: "Scop", type: "textarea" },
  { key: "duration", label: "Durată maximă" },
  { key: "type", label: "Tip (HTTP Cookie / Local Storage / …)" },
];

const legalItemFields = [
  { key: "heading", label: "Titlu secțiune" },
  { key: "body", label: "Conținut", type: "richtext" },
];

const testimonialItemFields = [
  { key: "quote", label: "Citată", type: "textarea" },
  { key: "name", label: "Nume" },
  { key: "username", label: "Username (@…)" },
  { key: "role", label: "Rol / funcție" },
  { key: "country", label: "Țară (emoji + text)" },
];

const serviceCardFields = [
  { key: "slug", label: "Slug (fix)", type: "select", options: ["social-media-management", "ads", "website", "consultanta"] },
  { key: "label", label: "Titlu card" },
  { key: "desc", label: "Descriere scurtă", type: "textarea" },
  {
    key: "cardLead",
    label: "Text lung card (opțional)",
    type: "textarea",
    hint: "Textul mic din partea de jos a cardului pe /servicii. Dacă e gol, se folosește automat lead-ul din pagina serviciului.",
  },
];

const portraitItemFields = [
  { key: "name", label: "Nume (overlay)" },
  { key: "role", label: "Rol (overlay)" },
  { key: "quote", label: "Citat scurt (opțional)", type: "textarea" },
  { key: "mediaType", label: "Tip media", type: "select", options: ["video", "image"] },
  {
    key: "mediaUrl",
    label: "URL video / imagine",
    type: "video",
    hint: "Video: MP4/WebM vertical 9:16 (ex. 1080×1920). Imagine: 1080×1920 px (9:16) — altfel se taie marginile.",
  },
  {
    key: "posterUrl",
    label: "Poster video (opțional)",
    type: "image",
    hint: "Recomandat: 1080×1920 px (9:16), același cadru ca video-ul.",
  },
];

const partnerItemFields = [
  { key: "name", label: "Nume brand (alt text)" },
  {
    key: "logoUrl",
    label: "Logo URL",
    type: "image",
    hint: "PNG/SVG fundal transparent, ~400×120 px lățime. Logo-ul nu se taie (object-contain).",
  },
  { key: "href", label: "Link (opțional)" },
];

const teamMemberFields = [
  {
    key: "image",
    label: "Poză",
    type: "image",
    hint: "Recomandat: 1200×1500 px (raport 4:5, portret) — la fel ca poza fondator.",
  },
  { key: "label", label: "Etichetă (ex. Fondator, Creative)" },
  { key: "name", label: "Nume" },
  { key: "role", label: "Funcție / rol" },
];

const statsItemFields = [
  { key: "title", label: "Titlu card" },
  { key: "desc", label: "Descriere", type: "textarea" },
];

const servicesTrustPointFields = [{ key: "value", label: "Text" }];

const servicesDetailedFields = [
  { key: "num", label: "Număr (ex. 01)" },
  { key: "title", label: "Titlu" },
  { key: "intro", label: "Intro", type: "textarea" },
  {
    key: "featuresText",
    label: "Features (un rând = un bullet)",
    type: "textarea",
    hint: "Separă bullet-urile prin Enter. Dacă e gol, se folosesc bullet-urile din cod (features).",
  },
];

const serviceWhyPointFields = [
  { key: "title", label: "Titlu" },
  { key: "desc", label: "Descriere", type: "textarea" },
];

const serviceFeatureFields = [
  { key: "title", label: "Titlu" },
  { key: "desc", label: "Descriere", type: "textarea" },
];

/** @type {Section[]} */
export const CUSTOMIZER_SECTIONS = [
  {
    id: "nav",
    page: "global",
    title: "Meniu principal",
    subtitle: "Header — toate paginile",
    domain: "translation",
    fields: [
      { path: "nav.home", label: "Acasă / Home" },
      { path: "nav.services", label: "Servicii" },
      { path: "nav.portfolio", label: "Portofoliu" },
      { path: "nav.about", label: "Despre" },
      { path: "nav.contact", label: "Contact" },
      { path: "nav.cta", label: "Buton CTA" },
    ],
  },
  {
    id: "footer",
    page: "global",
    title: "Footer",
    subtitle: "Toate paginile",
    domain: "translation",
    fields: [
      { path: "footer.tagline", label: "Tagline" },
      { path: "footer.explore", label: "„Explorează”" },
      { path: "footer.services_title", label: "Titlu coloană servicii" },
      { path: "footer.contact_title", label: "Titlu coloană contact" },
      { path: "footer.policies_title", label: "Titlu coloană politici" },
      { path: "footer.privacy_link", label: "Link confidențialitate" },
      { path: "footer.terms_link", label: "Link termeni" },
      { path: "footer.cookies_link", label: "Link cookie-uri" },
      { path: "footer.rights", label: "Drepturi de autor" },
      { path: "footer.made_with", label: "Made with / locație" },
    ],
  },
  {
    id: "contact-info",
    page: "global",
    title: "Date contact (globale)",
    subtitle: "Telefon, email în header / floating / footer",
    domain: "contact",
    fields: [
      { path: "email", label: "Email" },
      { path: "phone", label: "Telefon (afișat)" },
      { path: "companyName", label: "Denumire firmă (footer)" },
      { path: "companyCui", label: "CUI firmă (footer)" },
      { path: "instagram", label: "Instagram (@)" },
      { path: "footerLogoUrl", label: "Logo footer (URL), opțional" },
      { path: "socialInstagramUrl", label: "URL Instagram (footer)" },
      { path: "socialFacebookUrl", label: "URL Facebook" },
      { path: "socialTiktokUrl", label: "URL TikTok" },
      { path: "socialLinkedinUrl", label: "URL LinkedIn" },
    ],
  },
  {
    id: "home-settings",
    page: "home",
    title: "Setări secțiuni homepage",
    subtitle: "Afișare ON/OFF (global, nu se traduce)",
    domain: "translation",
    toggles: [
      { path: "homepageSettings.showTestimonials", label: "Afișează testimoniale (text)" },
      { path: "homepageSettings.showWhyUs", label: "Afișează „Excelența…” (De ce noi)" },
      { path: "homepageSettings.showPortrait", label: "Afișează showcase portrait" },
      { path: "homepageSettings.showPartners", label: "Afișează logo-uri parteneri" },
    ],
    globalFields: [
      {
        path: "homepageSettings.portraitScrollSeconds",
        label: "Viteză derulare portrait (secunde, mai mic = mai rapid; 0 = oprește)",
        type: "number",
        min: 0,
        max: 120,
      },
      {
        path: "homepageSettings.partnersScrollSeconds",
        label: "Viteză derulare logo-uri (secunde; 0 = oprește animația)",
        type: "number",
        min: 0,
        max: 120,
      },
    ],
  },
  {
    id: "hero",
    page: "home",
    title: "Hero",
    subtitle: "Secțiunea de sus",
    domain: "translation",
    fields: [
      { path: "hero.eyebrow", label: "Eyebrow" },
      { path: "hero.title_line1", label: "Titlu linia 1" },
      { path: "hero.title_line2", label: "Titlu linia 2" },
      { path: "hero.subtitle", label: "Subtitlu", type: "textarea" },
      { path: "hero.cta_primary", label: "Buton principal" },
      { path: "hero.cta_secondary", label: "Buton secundar" },
      { path: "hero.scroll", label: "Text scroll" },
    ],
  },
  {
    id: "marquee",
    page: "home",
    title: "Bandă defilantă (marquee)",
    subtitle: "Sub hero",
    domain: "translation",
    listEditor: {
      path: "marquee",
      itemLabel: "Cuvânt",
      itemFields: [{ key: "value", label: "Text" }],
      minItems: 1,
      maxItems: 12,
    },
  },
  {
    id: "why-us",
    page: "home",
    title: "Partenerul care livrează",
    subtitle: "Titlu + 4 carduri (De ce noi)",
    domain: "translation",
    fields: [{ path: "stats.subtitle", label: "Titlu secțiune" }],
    listEditor: {
      path: "stats.items",
      itemLabel: "Card",
      itemFields: statsItemFields,
      minItems: 1,
      maxItems: 6,
    },
  },
  {
    id: "home-services",
    page: "home",
    title: "Servicii (carduri)",
    subtitle: "4 carduri pe homepage",
    domain: "translation",
    fields: [
      { path: "services.eyebrow", label: "Eyebrow" },
      { path: "services.title", label: "Titlu" },
      { path: "services.cta", label: "Link „vezi toate”" },
    ],
    listEditor: {
      path: "serviceCards",
      itemLabel: "Card serviciu",
      itemFields: serviceCardFields,
      minItems: 1,
      maxItems: 6,
    },
  },
  {
    id: "process",
    page: "home",
    title: "Proces",
    subtitle: "4 pași",
    domain: "translation",
    fields: [
      { path: "process.eyebrow", label: "Eyebrow" },
      { path: "process.title", label: "Titlu" },
      { path: "process.steps.0.title", label: "Pas 1 — titlu" },
      { path: "process.steps.0.desc", label: "Pas 1 — descriere", type: "textarea" },
      { path: "process.steps.1.title", label: "Pas 2 — titlu" },
      { path: "process.steps.1.desc", label: "Pas 2 — descriere", type: "textarea" },
      { path: "process.steps.2.title", label: "Pas 3 — titlu" },
      { path: "process.steps.2.desc", label: "Pas 3 — descriere", type: "textarea" },
      { path: "process.steps.3.title", label: "Pas 4 — titlu" },
      { path: "process.steps.3.desc", label: "Pas 4 — descriere", type: "textarea" },
    ],
  },
  {
    id: "portrait-showcase",
    page: "home",
    title: "Showcase portrait",
    subtitle: "Video / poză — deasupra partenerilor",
    domain: "translation",
    fields: [
      { path: "portraitShowcase.eyebrow", label: "Eyebrow" },
      { path: "portraitShowcase.title", label: "Titlu" },
    ],
    listEditor: {
      path: "portraitShowcase.items",
      itemLabel: "Slide portrait",
      itemFields: portraitItemFields,
      minItems: 0,
      maxItems: 12,
    },
  },
  {
    id: "partners",
    page: "home",
    title: "Logo parteneri",
    subtitle: "Sub showcase portrait",
    domain: "translation",
    fields: [
      { path: "partners.eyebrow", label: "Eyebrow" },
      { path: "partners.title", label: "Titlu" },
    ],
    listEditor: {
      path: "partners.items",
      itemLabel: "Partener",
      itemFields: partnerItemFields,
      minItems: 0,
      maxItems: 24,
    },
  },
  {
    id: "testimonials",
    page: "home",
    title: "Testimoniale — antet",
    subtitle: "Titluri deasupra wall-ului 3D",
    domain: "translation",
    fields: [
      { path: "testimonials.eyebrow", label: "Eyebrow" },
      { path: "testimonials.title", label: "Titlu" },
    ],
  },
  {
    id: "testimonials-cards",
    page: "home",
    title: "Testimoniale — carduri",
    subtitle: "Citate clienți",
    domain: "translation",
    listEditor: {
      path: "testimonials.items",
      itemLabel: "Testimonial",
      itemFields: testimonialItemFields,
      minItems: 1,
      maxItems: 20,
    },
  },
  {
    id: "cta-home",
    page: "home",
    title: "CTA final",
    subtitle: "Înainte de footer",
    domain: "translation",
    fields: [
      { path: "cta_section.eyebrow", label: "Eyebrow" },
      { path: "cta_section.title", label: "Titlu" },
      { path: "cta_section.subtitle", label: "Subtitlu", type: "textarea" },
      { path: "cta_section.button", label: "Text buton" },
    ],
  },
  {
    id: "about-images",
    page: "about",
    title: "Imagini pagină Despre",
    subtitle: "Poză fondator + secțiune colaborare — /despre",
    domain: "translation",
    fields: [
      {
        path: "about.team_image",
        label: "Poză fondator / echipă",
        type: "image",
        hint: "Recomandat: 1200×1500 px (raport 4:5, portret). Cu acest raport imaginea se vede complet, fără tăiere.",
      },
      {
        path: "about.section_image",
        label: "Imagine secțiune strategie",
        type: "image",
        hint: "Recomandat: 1600×1200 px (raport 4:3, landscape). Cu acest raport imaginea se vede complet, fără tăiere.",
      },
    ],
  },
  {
    id: "about",
    page: "about",
    title: "Pagina Despre",
    subtitle: "/despre",
    domain: "translation",
    fields: [
      { path: "about.eyebrow", label: "Eyebrow" },
      { path: "about.title", label: "Titlu principal" },
      { path: "about.lead", label: "Lead", type: "textarea" },
      { path: "about.p1", label: "Paragraf 1", type: "textarea" },
      { path: "about.p2", label: "Paragraf 2", type: "textarea" },
      { path: "about.team_title", label: "Titlu echipă" },
      { path: "about.team_name", label: "Nume echipă / fondator" },
      { path: "about.team_role", label: "Rol" },
      { path: "about.team_bio", label: "Bio", type: "textarea" },
      { path: "about.values_title", label: "Titlu valori" },
      { path: "about.section_image_title", label: "Secțiune imagine — titlu" },
      { path: "about.section_image_body", label: "Secțiune imagine — text", type: "textarea" },
    ],
  },
  {
    id: "about-team",
    page: "about",
    title: "Echipă (showcase)",
    subtitle: "Grid desktop + carousel mobil — sub secțiunea strategie",
    domain: "translation",
    fields: [
      { path: "about.team_showcase.eyebrow", label: "Eyebrow secțiune echipă" },
      { path: "about.team_showcase.title", label: "Titlu secțiune echipă" },
    ],
    listEditor: {
      path: "about.team_members",
      itemLabel: "Membru echipă",
      itemFields: teamMemberFields,
      minItems: 0,
      maxItems: 8,
    },
  },
  {
    id: "services-page",
    page: "services",
    title: "Pagina Servicii (overview)",
    subtitle: "/servicii",
    domain: "translation",
    fields: [
      { path: "services_page.eyebrow", label: "Eyebrow" },
      { path: "services_page.title", label: "Titlu" },
      { path: "services_page.subtitle", label: "Subtitlu", type: "textarea" },
      { path: "services_page.expertise_kicker", label: "Secțiune expertiză — kicker (ex. 01 · Expertiză)" },
      { path: "services_page.expertise_title", label: "Secțiune expertiză — titlu (Patru piloni…)" },
      { path: "services_page.cta_title", label: "CTA — titlu" },
      { path: "services_page.cta_subtitle", label: "CTA — subtitlu", type: "textarea" },
      { path: "services_page.cta_button", label: "CTA — buton" },
    ],
    listEditors: [
      {
        path: "services_page.trust_points",
        itemLabel: "Punct de încredere",
        itemFields: servicesTrustPointFields,
        minItems: 0,
        maxItems: 6,
      },
      {
        path: "services_page.detailed",
        itemLabel: "Card detaliat",
        itemFields: servicesDetailedFields,
        minItems: 0,
        maxItems: 8,
      },
    ],
  },
  {
    id: "services-cards",
    page: "services",
    title: "Carduri servicii (grid 4)",
    subtitle: "Textele de pe cele 4 carduri mari",
    domain: "translation",
    listEditor: {
      path: "serviceCards",
      itemLabel: "Card serviciu",
      itemFields: serviceCardFields,
      minItems: 1,
      maxItems: 6,
    },
  },
  {
    id: "portfolio",
    page: "portfolio",
    title: "Portofoliu",
    subtitle: "/portofoliu",
    domain: "translation",
    fields: [
      { path: "portfolio.eyebrow", label: "Eyebrow" },
      { path: "portfolio.title", label: "Titlu" },
      { path: "portfolio.subtitle", label: "Subtitlu", type: "textarea" },
      { path: "portfolio.cta", label: "Buton CTA" },
    ],
    listEditor: {
      path: "portfolio.items",
      itemLabel: "Proiect",
      itemFields: [
        { key: "name", label: "Nume proiect" },
        { key: "service", label: "Serviciu" },
        {
          key: "image",
          label: "Imagine / poster video",
          type: "image",
          hint: "Recomandat: 1000×1250 px (4:5). Folosit ca imagine sau poster când ai video.",
        },
        {
          key: "video",
          label: "Video (opțional)",
          type: "video",
          hint: "MP4/WebM, max ~25 MB. Dacă e setat, video-ul rulează muted + autoplay pe portofoliu.",
        },
      ],
      minItems: 0,
      maxItems: 12,
    },
  },
  {
    id: "contact-page",
    page: "contact",
    title: "Pagina Contact",
    subtitle: "/contact",
    domain: "translation",
    fields: [
      { path: "contact.eyebrow", label: "Eyebrow" },
      { path: "contact.title", label: "Titlu" },
      { path: "contact.subtitle", label: "Subtitlu", type: "textarea" },
      { path: "contact.form.name", label: "Form — label nume" },
      { path: "contact.form.email", label: "Form — label email" },
      { path: "contact.form.phone", label: "Form — label telefon" },
      { path: "contact.form.message", label: "Form — label mesaj" },
      { path: "contact.form.submit", label: "Form — trimite" },
      { path: "contact.form.success", label: "Form — mesaj succes" },
      { path: "contact.form.rate_limited", label: "Form — prea multe cereri", type: "textarea" },
      { path: "contact.info_title", label: "Titlu „alte căi”" },
      { path: "contact.location_value", label: "Valoare locație" },
      { path: "contact.hours_value", label: "Valoare program" },
    ],
  },
  {
    id: "legal-privacy",
    page: "legal",
    title: "Politica de Confidențialitate",
    subtitle: "/politica-confidentialitate",
    domain: "translation",
    fields: [
      { path: "legal.privacy.title", label: "Titlu pagină" },
      { path: "legal.privacy.updated", label: "Dată actualizare" },
    ],
    listEditor: {
      path: "legal.privacy.sections",
      itemLabel: "Secțiune",
      itemFields: legalItemFields,
      minItems: 1,
      maxItems: 20,
    },
  },
  {
    id: "legal-terms",
    page: "legal",
    title: "Termeni și Condiții",
    subtitle: "/termeni-si-conditii",
    domain: "translation",
    fields: [
      { path: "legal.terms.title", label: "Titlu pagină" },
      { path: "legal.terms.updated", label: "Dată actualizare" },
    ],
    listEditor: {
      path: "legal.terms.sections",
      itemLabel: "Secțiune",
      itemFields: legalItemFields,
      minItems: 1,
      maxItems: 20,
    },
  },
  {
    id: "cookie-declaration",
    page: "legal",
    title: "Declarație cookie-uri (banner)",
    subtitle: "Tabel cookie + categorii pe /politica-cookie-uri",
    domain: "translation",
    fields: [
      { path: "cookieDeclaration.title", label: "Titlu declarație" },
      { path: "cookieDeclaration.intro", label: "Introducere", type: "textarea" },
      { path: "cookieDeclaration.consent_note", label: "Notă consimțământ / contact", type: "textarea" },
      { path: "cookieDeclaration.last_updated", label: "Dată actualizare declarație" },
    ],
    listEditor: {
      path: "cookieDeclaration.categories",
      itemLabel: "Categorie",
      itemFields: cookieCategoryFields,
      minItems: 4,
      maxItems: 6,
    },
  },
  {
    id: "cookie-inventory",
    page: "legal",
    title: "Inventar cookie-uri",
    subtitle: "Rânduri din tabelul declarației",
    domain: "translation",
    listEditor: {
      path: "cookieDeclaration.cookies",
      itemLabel: "Cookie",
      itemFields: cookieItemFields,
      minItems: 1,
      maxItems: 30,
    },
  },
  {
    id: "legal-cookies",
    page: "legal",
    title: "Politica de Cookie-uri",
    subtitle: "/politica-cookie-uri",
    domain: "translation",
    fields: [
      { path: "legal.cookies.title", label: "Titlu pagină" },
      { path: "legal.cookies.updated", label: "Dată actualizare" },
    ],
    listEditor: {
      path: "legal.cookies.sections",
      itemLabel: "Secțiune",
      itemFields: legalItemFields,
      minItems: 1,
      maxItems: 20,
    },
  },
  ...["social-media-management", "ads", "website", "consultanta"].map((slug) => {
    const svcTitles = {
      "social-media-management": "Social Media Management",
      ads: "Campanii Ads",
      website: "Websites",
      consultanta: "Consultanță",
    };
    return {
      id: `svc-${slug}`,
      page: "services",
      title: `Serviciu: ${svcTitles[slug] || slug}`,
      subtitle: `/servicii/${slug}`,
      domain: "servicePage",
      slug,
      fields: [
        { path: "icon", label: "Icon (camera / megaphone / monitor / compass)" },
        { path: "eyebrow", label: "Eyebrow (deasupra titlului)" },
        {
          path: "intro_image",
          label: "Imagine secțiune „Prezentare”",
          type: "image",
          hint: "Recomandat: 1600×1200 px (raport 4:3). Cu acest raport imaginea se vede complet pe pagina serviciului.",
        },
        { path: "title", label: "Titlu principal" },
        { path: "lead", label: "Lead", type: "textarea" },
        { path: "intro_title", label: "Intro — titlu" },
        { path: "intro_body", label: "Intro — text", type: "textarea" },
        { path: "why_title", label: "De ce noi — titlu" },
        { path: "features_title", label: "Livrabile — titlu" },
        { path: "cta_title", label: "CTA — titlu" },
        { path: "cta_subtitle", label: "CTA — subtitlu", type: "textarea" },
      ],
      listEditors: [
        {
          path: "why_points",
          itemLabel: "Card „De ce noi”",
          itemFields: serviceWhyPointFields,
          minItems: 0,
          maxItems: 8,
        },
        {
          path: "features",
          itemLabel: "Livrabil",
          itemFields: serviceFeatureFields,
          minItems: 0,
          maxItems: 12,
        },
      ],
    };
  }),
];
